import { supabase } from '../lib/supabase';

export const workerService = {
  async getAllWorkers() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          full_name,
          role,
          is_active,
          invited_by,
          company_id,
          created_at,
          updated_at
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Users & Teams page (9i4dXP36). Returns per-user profile data plus
  // aggregated assigned-site ids/names and auth.users.last_sign_in_at,
  // scoped to the caller's company via the SECURITY DEFINER RPC. Keep
  // getAllWorkers / getWorkersByCompany untouched for back-compat with
  // other callers.
  async getCompanyUsersWithMeta(companyId) {
    try {
      const { data, error } = await supabase?.rpc('get_company_users_with_meta', {
        p_company_id: companyId,
      });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getWorkersByCompany(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          full_name,
          role,
          is_active,
          invited_by,
          company_id,
          created_at,
          updated_at
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getWorkerById(workerId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          full_name,
          role,
          is_active,
          invited_by,
          company_id,
          deactivated_at,
          deactivated_by,
          created_at,
          updated_at
        `)
        ?.eq('id', workerId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getWorkerSiteAssignments(workerId) {
    try {
      const { data, error } = await supabase
        ?.from('site_workers')
        ?.select(`
          site_id,
          assigned_date,
          is_active,
          sites (
            id,
            site_name,
            client_name,
            status
          )
        `)
        ?.eq('worker_id', workerId)
        ?.eq('is_active', true)
        ?.order('assigned_date', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getWorkerActivities(workerId, limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('activities')
        ?.select(`
          id,
          activity_type,
          title,
          description,
          created_at,
          site_id,
          sites (
            site_name
          )
        `)
        ?.eq('user_id', workerId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Per-worker task completion rate over the last `days` window for a
   * company. Returns one row per worker:
   *   { user_id, full_name, role, completion_pct, completion_days,
   *     scheduled_days }
   *
   * Definition (Heidi 24-Apr): the % of days, in the window, on which the
   * worker completed every task assigned to them. Days where the worker
   * had no tasks assigned are excluded — they're noise, not a completion
   * signal. `completion_pct` is `null` when the worker had no tasks in
   * the window at all; the UI renders "Not yet scheduled".
   *
   * Source-of-truth is daily_tasks alone. assigned_to_user_id is NOT NULL
   * in schema, so every task has a direct owner — we don't need to
   * cross-join worker_site_assignments. Sorted highest → lowest, with
   * "no data" workers at the bottom.
   *
   * RLS: admins/site_managers can SELECT all daily_tasks (per
   * daily_tasks_select policy) and all user_profiles for their company
   * (per company_members_view_profiles). No RPC needed.
   */
  async getCompletionRates(companyId, days = 30) {
    try {
      if (!companyId) return { data: [], error: null };
      const since = new Date(Date.now() - days * 86400000)
        ?.toISOString()
        ?.split('T')?.[0];

      const { data: workers, error: workersError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role')
        ?.eq('company_id', companyId)
        ?.eq('role', 'worker')
        ?.eq('is_active', true);
      if (workersError) throw workersError;
      if (!workers?.length) return { data: [], error: null };

      const workerIds = workers?.map(w => w?.id);
      const { data: tasks, error: tasksError } = await supabase
        ?.from('daily_tasks')
        ?.select('assigned_to_user_id, task_date, status, is_completed')
        ?.eq('company_id', companyId)
        ?.in('assigned_to_user_id', workerIds)
        ?.gte('task_date', since);
      if (tasksError) throw tasksError;

      const byWorker = new Map();
      for (const w of workers) byWorker.set(w?.id, new Map());
      for (const task of (tasks || [])) {
        const dateMap = byWorker.get(task?.assigned_to_user_id);
        if (!dateMap) continue;
        const day = dateMap.get(task?.task_date) || { total: 0, completed: 0 };
        day.total += 1;
        if (task?.status === 'completed' || task?.is_completed === true) {
          day.completed += 1;
        }
        dateMap.set(task?.task_date, day);
      }

      const rows = workers?.map(w => {
        const dateMap = byWorker.get(w?.id) || new Map();
        const dayBuckets = [...dateMap.values()];
        const scheduledDays = dayBuckets?.length;
        const completionDays = dayBuckets?.filter(
          d => d?.total > 0 && d?.completed === d?.total
        )?.length;
        const completionPct = scheduledDays > 0
          ? Math.round((completionDays / scheduledDays) * 100)
          : null;
        return {
          user_id: w?.id,
          full_name: w?.full_name,
          role: w?.role,
          scheduled_days: scheduledDays,
          completion_days: completionDays,
          completion_pct: completionPct,
        };
      });

      rows?.sort((a, b) => {
        if (a?.completion_pct === null && b?.completion_pct === null) {
          return (a?.full_name || '')?.localeCompare(b?.full_name || '');
        }
        if (a?.completion_pct === null) return 1;
        if (b?.completion_pct === null) return -1;
        return b?.completion_pct - a?.completion_pct;
      });

      return { data: rows, error: null };
    } catch (error) {
      console.error('[workerService] getCompletionRates failed:', {
        code: error?.code,
        message: error?.message,
      });
      return { data: [], error };
    }
  },

  async getWorkerStats(workerId) {
    try {
      const [documentsResult, checklistsResult, incidentsResult, auditsResult] = await Promise.all([
        // NSherjFh PR 2: worker stats exclude soft-deleted docs.
        supabase?.from('documents')?.select('id', { count: 'exact', head: true })?.eq('uploaded_by', workerId)?.is('deleted_at', null),
        supabase?.from('checklist_completions')?.select('id', { count: 'exact', head: true })?.eq('completed_by', workerId),
        // NSherjFh PR 5: worker stats exclude soft-deleted incidents.
        supabase?.from('incidents')?.select('id', { count: 'exact', head: true })?.eq('reported_by', workerId)?.is('deleted_at', null),
        supabase?.from('audits')?.select('id', { count: 'exact', head: true })?.eq('auditor_id', workerId)
      ]);

      return {
        data: {
          documentsUploaded: documentsResult?.count || 0,
          checklistsCompleted: checklistsResult?.count || 0,
          incidentsLogged: incidentsResult?.count || 0,
          auditsParticipated: auditsResult?.count || 0
        },
        error: null
      };
    } catch (error) {
      return {
        data: {
          documentsUploaded: 0,
          checklistsCompleted: 0,
          incidentsLogged: 0,
          auditsParticipated: 0
        },
        error: { message: error?.message }
      };
    }
  }
};